import type { ComponentProps } from "react";
import React from "react";

import { Pagination, PaginationInput } from "@similarweb/ui-components";

import { useDidMountEffect } from "app/custom-hooks/useDidMountEffect";
import type { INumberRange } from "app/UtilitiesAndConstants/types/range";

interface IUsePaginationArgs {
    itemsPerPage?: number;
    initialPage?: number;
    callbackOnPageChange?: (page: number, pageItems: INumberRange) => void;
    listItemsLength: number;
}

export const usePagination = ({
    itemsPerPage = 5,
    initialPage = 1,
    callbackOnPageChange,
    listItemsLength,
}: IUsePaginationArgs): [INumberRange, React.ReactElement<ComponentProps<typeof Pagination>>, number] => {
    const totalPages = Math.ceil(listItemsLength / itemsPerPage);

    const [page, setPage] = React.useState<number>(initialPage);

    React.useEffect(() => {
        setPage(initialPage);
    }, [listItemsLength]);

    const isValidPageNumber = (page: number) => page <= totalPages && page > 0;

    const handlePageChange = (page: number) => {
        if (isValidPageNumber(page)) {
            setPage(page);
        }
    };
    const rangeFrom = itemsPerPage * (page - 1);
    const rangeTo = rangeFrom + itemsPerPage;
    const pageItems = { from: rangeFrom, to: rangeTo };

    useDidMountEffect(() => {
        callbackOnPageChange?.(page, pageItems);
    }, [page]);

    const paginationComponent = (
        <Pagination
            page={page}
            itemsCount={listItemsLength}
            itemsPerPage={itemsPerPage}
            handlePageChange={handlePageChange}
            captionPosition="center"
            hasItemsPerPageSelect={false}
            captionElement={PaginationInput}
        />
    );
    return [pageItems, paginationComponent, page];
};
